/**
 * Debug-only feature flags for SleepDetailView.
 *
 * Toggling these on/off is the primary way we expose work-in-progress
 * diagnostics (extra overlays, raw-data download buttons, side-by-side vitals
 * charts, etc.) without shipping them to the patient-facing UI.
 *
 * NOT a runtime config — values are baked in at build time. Flip a flag,
 * reload, verify.
 */

/** Prod origin used by the debug CDL download buttons that bypass the
 *  hexagon and hit the backend directly. Not user-facing. */
export const PROD_ORIGIN = "https://prod.circadia.link";

/** Verbose console.log/warn from the sleep-detail view (calculation traces,
 *  cache hits, chart re-renders, etc.). */
export const DEBUG_SLEEP_DETAIL_LOGS = false;

/** Show the JSON diagnostic textarea (per-hour sleep-window detail, backend
 *  vs frontend merge trace, stage breakdown, HR/RR counts). */
export const DEBUG_SLEEP_DETAIL_PANEL = false;

/** Overlay a semi-transparent illumination line on top of the sleep bar
 *  chart so you can correlate light exposure with sleep quality. */
export const DEBUG_SLEEP_DETAIL_ILLUM_OVERLAY = false;

/** Color bars BLUE when the backend inference score was higher and drove
 *  the merged final value (vs the normal quality-based green/amber/orange). */
export const DEBUG_SLEEP_DETAIL_BACKEND_BAR_COLOR = false;

/** Show "Time in bed" as a native tooltip on the sleep efficiency ring. */
export const DEBUG_SLEEP_EFFICIENCY_TIME_IN_BED_TOOLTIP = false;

/** Show the 🔄 button next to the date label on the detail pages (Sleep, RR,
 *  HR) that drops the cached CDL data and forces a fresh re-fetch +
 *  recalculation. Sleep busts its cdlRangeCache + window snapshot; RR/HR
 *  revalidate their SWR caches. */
export const DEBUG_DETAIL_REFRESH_BUTTON = false;

/** Show the "Download 1 week of 3s CDL" and "Download 1 week of 3s HR"
 *  buttons in the debug panel. Hits `/cdl-data` directly. */
export const DEBUG_SLEEP_DETAIL_DOWNLOAD_CDL_BUTTON = false;

/** Vital charts (RR / HR): instead of ERASING sensor-artifact hours (hours with
 *  too few raw 3s points — see ProdConstants.tsx), keep them and paint their bar
 *  red so you can see exactly what the filter would remove. Diagnostic only —
 *  set false to actually erase artifact hours as in production. */
export const DEBUG_VITALS_HIGHLIGHT_ARTIFACT_HOURS = false;

/** Day view: fetch the raw 3s CDL (rr_3s + hr_3s) for the selected day and show
 *  the per-hour count of non-null 3s data points (RR + HR) in the sleep bar
 *  tooltip. Hits /cdl-data directly and pulls a multi-megabyte response, so this
 *  is debug-only. When off, the tooltip falls back to the (hourly, far less
 *  useful) sample count the calculator consumed. */
export const DEBUG_SLEEP_DETAIL_3S_TOOLTIP_COUNT = false;

/** Render a line chart of respiratory rate (rpm) below the sleep bar
 *  chart, x-axis aligned with the bars for visual correlation. */
export const DEBUG_SLEEP_DETAIL_RR_CHART = false;

/** Render a line chart of heart rate (bpm) below the sleep bar chart,
 *  x-axis aligned with the bars for visual correlation. */
export const DEBUG_SLEEP_DETAIL_HR_CHART = false;

/** Continuous (hour-by-hour) scroll for the respiratory-rate Day tab. Only
 *  takes effect when SHOW_RR_DAILY_HISTORY is ALSO enabled: instead of paging
 *  a whole calendar day at a time, the prev/next arrows slide the 24-hour
 *  window by 1 hour, letting you scrub continuously across the ~7 days of
 *  hourly RR history. */
export const DEBUG_RR_CONTINUOUS_SCROLL = false;

/** Continuous (hour-by-hour) scroll for the heart-rate Day tab. See
 *  DEBUG_RR_CONTINUOUS_SCROLL — requires SHOW_HR_DAILY_HISTORY to be on too. */
export const DEBUG_HR_CONTINUOUS_SCROLL = false;

/** Show prev/next arrows on the sleep detail view's Day (D) tab so you can
 *  page back through previous nights (one calendar day at a time). Sleep
 *  already supports historical day navigation, so this is mostly here to mirror
 *  the RR/HR pattern and to gate the continuous-scroll flag below. */
export const DEBUG_SLEEP_DAILY_HISTORY = false;

/** Continuous (hour-by-hour) scroll for the sleep Day tab. Only takes effect
 *  when DEBUG_SLEEP_DAILY_HISTORY is ALSO enabled. When on, the prev/next
 *  arrows slide a 24-hour window by 1 hour and the sleep window, composite
 *  score, and all clinical metrics are FULLY RECOMPUTED for whatever 24h span
 *  is in view (no overnight assumption). Lets you scrub continuously across
 *  the last several days of sleep data. */
export const DEBUG_SLEEP_CONTINUOUS_SCROLL = false;

/** Force the Health Overview vital cards (Respiratory, Heart Rate, Sleep) to be
 *  clickable even when no device is assigned / connected. Normally those cards
 *  are disabled (and route navigation blocked) in the "No device connected"
 *  state. With an identity override active you're viewing another patient's
 *  data while the logged-in account may have no device, so this lets you still
 *  drill into the RR / HR / Sleep detail pages. */
export const DEBUG_FORCE_VITAL_CARDS_CLICKABLE = false;

/** Bypass the "Access Restricted" screen (shown when `auth.hasNoAccess` is true
 *  — i.e. the logged-in account type isn't permitted in this app). When true,
 *  the router ignores `hasNoAccess` and falls through to the normal patient app
 *  shell. Pairs with the identity overrides so you can inspect another
 *  patient's data from an account that would otherwise be blocked. */
export const DEBUG_BYPASS_ACCESS_RESTRICTED = false;

// ── Identity overrides (debug) ─────────────────────────────────
// When non-null, these REPLACE the real patient / clinic / device identity that
// the app normally derives from Redux (settings.notifications.patient_id,
// clinic.userClinic.id, clinic.assignedDevice.device_id). This lets you view
// another patient's data in the patient-facing app without logging in as them.
// Leave null for normal behavior — an override only takes effect when set.
//
// These supersede the old `STATIC_PATIENT_ID` config fallbacks (which were
// inconsistently applied and only kicked in when no patient was logged in). An
// override here wins even when a real patient IS logged in.

/** Force a specific patient id everywhere data is fetched. Null = use the
 *  logged-in patient. */
export const DEBUG_OVERRIDE_PATIENT_ID: number | null = null;

/** Force a specific clinic id everywhere data is fetched. Null = use the
 *  logged-in user's clinic. */
export const DEBUG_OVERRIDE_CLINIC_ID: number | null = null;

/** Force a specific device id for the sleep CDL / inference fetches. MUST be the
 *  normalized form: 12 lowercase hex chars, no dashes (e.g. "a1b2c3d4e5f6").
 *  Null = use the assigned device. */
export const DEBUG_OVERRIDE_DEVICE_ID: string | null = null;

/** Effective patient id: the debug override when set, otherwise `actual`. */
export function resolvePatientId(
  actual: number | undefined
): number | undefined {
  return DEBUG_OVERRIDE_PATIENT_ID ?? actual;
}

/** Effective clinic id: the debug override when set, otherwise `actual`. */
export function resolveClinicId(
  actual: number | undefined
): number | undefined {
  return DEBUG_OVERRIDE_CLINIC_ID ?? actual;
}

/** Effective (normalized) device id: the debug override when set, otherwise
 *  `actual`. */
export function resolveDeviceId(
  actual: string | undefined
): string | undefined {
  return DEBUG_OVERRIDE_DEVICE_ID ?? actual;
}

/** True when ANY identity override (patient/clinic/device) is currently set.
 *  Used to surface a visible "DEVELOPER" indicator so it's obvious the app is
 *  showing hard-wired data rather than the logged-in patient's. */
export function isIdentityOverrideActive(): boolean {
  return (
    DEBUG_OVERRIDE_PATIENT_ID !== null ||
    DEBUG_OVERRIDE_CLINIC_ID !== null ||
    DEBUG_OVERRIDE_DEVICE_ID !== null
  );
}
